import type { IchimokuLines } from "../../model/calcIchimoku";
import type { IchimokuKinkoHyoSettings } from "../../model";
import type { ZoomRange, CandleWithFlag } from "features/chart";
import { GRID, CANDLE_MIN_WIDTH } from "features/chart";

export function drawIchimoku(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  data: CandleWithFlag[],
  zoom: ZoomRange,
  lines: IchimokuLines,
  settings: IchimokuKinkoHyoSettings,
): void {
  const { left, right, top, bottom } = GRID;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const { start, end } = zoom;
  const startIdx = Math.floor(start);
  const endIdx = Math.min(Math.floor(end), data.length - 1);
  const visibleCount = Math.floor(end) - startIdx + 1;
  const cw = Math.max(CANDLE_MIN_WIDTH, chartWidth / visibleCount);

  let minPrice = Infinity, maxPrice = -Infinity;
  for (let i = startIdx; i <= endIdx; i++) {
    const c = data[i]; if (!c) continue;
    minPrice = Math.min(minPrice, c.low);
    maxPrice = Math.max(maxPrice, c.high);
  }
  if (minPrice === Infinity) return;
  const priceRange = maxPrice - minPrice || 1;
  minPrice -= priceRange * 0.05;
  maxPrice += priceRange * 0.05;
  const range = maxPrice - minPrice;
  const priceToY = (p: number) => top + ((maxPrice - p) / range) * chartHeight;
  const getX = (i: number) => left + (i - startIdx + 0.5) * cw;

  ctx.clearRect(0, 0, width, height);

  const cloudEnd = Math.min(Math.floor(end), lines.senkouSpanA.length - 1);

  const fillCloud = (color: string, above: (number | null)[], below: (number | null)[]) => {
    ctx.save();
    ctx.globalAlpha = 0.15;
    ctx.fillStyle = color;
    let s = -1;
    const flush = (e: number) => {
      if (s === -1) return;
      ctx.beginPath();
      for (let i = s; i <= e; i++) {
        const v = above[i]; if (v === null) return;
        i === s ? ctx.moveTo(getX(i), priceToY(v)) : ctx.lineTo(getX(i), priceToY(v));
      }
      for (let i = e; i >= s; i--) { const v = below[i]; if (v !== null) ctx.lineTo(getX(i), priceToY(v)); }
      ctx.closePath(); ctx.fill(); s = -1;
    };
    for (let i = startIdx; i <= cloudEnd; i++) {
      const a = above[i], b = below[i];
      const on = a !== null && b !== null && a >= b;
      if (on && s === -1) s = i;
      else if (!on && s !== -1) flush(i - 1);
    }
    flush(cloudEnd);
    ctx.restore();
  };

  fillCloud(settings.senkouSpanA.color, lines.senkouSpanA, lines.senkouSpanB);
  fillCloud(settings.senkouSpanB.color, lines.senkouSpanB, lines.senkouSpanA);

  const drawLine = (values: (number | null)[], lineEnd: number, color: string, lw: number) => {
    ctx.strokeStyle = color; ctx.lineWidth = lw;
    ctx.beginPath(); let started = false;
    for (let i = startIdx; i <= lineEnd; i++) {
      const v = values[i];
      if (v === null) { started = false; continue; }
      if (!started) { ctx.moveTo(getX(i), priceToY(v)); started = true; }
      else ctx.lineTo(getX(i), priceToY(v));
    }
    ctx.stroke();
  };

  const { tenkanSen: ts, kijunSen: ks, chikouSpan: cs, senkouSpanA: sa, senkouSpanB: sb } = settings;
  if (ts.visible) drawLine(lines.tenkanSen, endIdx, ts.color, ts.width);
  if (ks.visible) drawLine(lines.kijunSen, endIdx, ks.color, ks.width);
  if (cs.visible) drawLine(lines.chikouSpan, endIdx, cs.color, cs.width);
  if (sa.visible) drawLine(lines.senkouSpanA, cloudEnd, sa.color, sa.width);
  if (sb.visible) drawLine(lines.senkouSpanB, cloudEnd, sb.color, sb.width);
}
